import React from 'react';
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { iTicketingSetup } from 'services/Ticketing/Ticketing.type';
import { card, sectionTitle } from '../common';

interface TicketDesignSectionProps {
  data: iTicketingSetup;
  form: Omit<iTicketingSetup['settings'], 'logo_url'>;
  lang: 'fr' | 'en';
  set: <K extends keyof Omit<iTicketingSetup['settings'], 'logo_url'>>(
    key: K,
    value: Omit<iTicketingSetup['settings'], 'logo_url'>[K]
  ) => void;
  setLang: (lang: 'fr' | 'en') => void;
  run: (p: Promise<iTicketingSetup>, ok: string) => void;
  ticketingRepository: any;
  eventId: string | number;
  preview: (typeId?: number) => void;
}

/** Upload slot for the banner background or the venue plan. */
function ImageSlot({
  title,
  hint,
  url,
  wide,
  onUpload,
  onRemove,
}: {
  title: string;
  hint: string;
  url: string | null | undefined;
  wide?: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
}): JSX.Element {
  return (
    <Box>
      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#111' }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '12px', color: '#888', mb: 1 }}>
        {hint}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {url ? (
          <img
            src={url}
            alt={title}
            style={{
              width: wide ? 220 : 120,
              height: wide ? 44 : 90,
              objectFit: wide ? 'cover' : 'contain',
              border: '1px solid #eee',
              background: '#fafafa',
            }}
          />
        ) : null}
        <Button component="label" size="small" sx={{ textTransform: 'none' }}>
          {url ? 'Remplacer' : 'Ajouter une image'}
          <input
            hidden
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = '';
            }}
          />
        </Button>
        {url && (
          <Button
            size="small"
            sx={{ textTransform: 'none', color: '#666' }}
            onClick={onRemove}
          >
            Retirer
          </Button>
        )}
      </Box>
    </Box>
  );
}

/** "Repris d'EM" under a field, or a link to go back to EM's value. */
function FromEm({
  value,
  em,
  onReset,
}: {
  value: string;
  em: string | null | undefined;
  onReset: () => void;
}): JSX.Element | null {
  if (!em) return null;
  const same = value.trim().toLowerCase() === em.trim().toLowerCase();
  return (
    <Typography sx={{ fontSize: '12px', color: '#888', mt: 0.5 }}>
      {same ? (
        <>Repris de l&apos;événement dans EM (suit ses modifications).</>
      ) : (
        <>
          Modifié pour la billetterie.{' '}
          <Box
            component="button"
            type="button"
            onClick={onReset}
            sx={{
              background: 'none',
              border: 0,
              p: 0,
              color: '#1976d2',
              cursor: 'pointer',
              fontSize: '12px',
              fontFamily: 'inherit',
            }}
          >
            Reprendre la valeur d&apos;EM ({em})
          </Box>
        </>
      )}
    </Typography>
  );
}

export function TicketDesignSection({
  data,
  form,
  lang,
  set,
  setLang,
  run,
  ticketingRepository,
  eventId,
  preview,
}: TicketDesignSectionProps): JSX.Element {
  const s = data.settings;

  return (
    <Box sx={{ ...card, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography sx={sectionTitle}>Le billet</Typography>

      {/* Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {s.logo_url ?? s.em_logo_url ? (
          <img
            src={(s.logo_url ?? s.em_logo_url) as string}
            alt="Logo"
            style={{
              height: 56,
              maxWidth: 150,
              objectFit: 'contain',
              border: '1px solid #eee',
              padding: 4,
              // PNG: previewed on the ticket colour, as on the banner
              background: s.logo_transparent ? form.accent_color : '#fff',
            }}
          />
        ) : (
          <Box
            sx={{
              height: 56,
              width: 110,
              border: '1px dashed #ccc',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              color: '#999',
              textAlign: 'center',
            }}
          >
            Aucun logo dans EM
          </Box>
        )}
        <Button component="label" size="small" sx={{ textTransform: 'none' }}>
          {s.logo_url
            ? 'Changer le logo'
            : s.em_logo_url
            ? 'Remplacer le logo du salon'
            : 'Ajouter un logo'}
          <input
            hidden
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f)
                run(
                  ticketingRepository.uploadLogo(eventId, f),
                  'Logo enregistré.'
                );
            }}
          />
        </Button>
        {s.logo_url && (
          <Button
            size="small"
            sx={{ textTransform: 'none', color: '#666' }}
            onClick={() =>
              run(ticketingRepository.deleteLogo(eventId), 'Logo retiré.')
            }
          >
            Retirer
          </Button>
        )}
      </Box>

      {/* Ticket title (event name + year) and subtitle, from EM */}
      <Box>
        <TextField
          size="small"
          fullWidth
          label="Titre sur le billet"
          placeholder={data.em?.title ?? data.event.name}
          value={form.ticket_title}
          onChange={(e) => set('ticket_title', e.target.value)}
        />
        <FromEm
          value={form.ticket_title}
          em={data.em?.title}
          onReset={() => set('ticket_title', data.em?.title ?? '')}
        />
      </Box>
      <Box>
        <TextField
          size="small"
          fullWidth
          label="Sous-titre"
          value={data.em?.subtitle ?? ''}
          placeholder="Aucun sous-titre dans EM"
          disabled
        />
        <Typography sx={{ fontSize: '12px', color: '#888', mt: 0.5 }}>
          Sous-titre de l&apos;événement, à modifier dans « Info de
          l&apos;événement ».
        </Typography>
      </Box>

      {/* Accent color */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <input
          type="color"
          value={form.accent_color}
          onChange={(e) => set('accent_color', e.target.value)}
          style={{
            width: 48,
            height: 38,
            border: '1px solid #ddd',
            borderRadius: 6,
          }}
        />
        <TextField
          size="small"
          label="Couleur"
          value={form.accent_color}
          onChange={(e) => set('accent_color', e.target.value)}
          sx={{ width: 140 }}
        />
      </Box>
      <Box sx={{ mt: -1.5 }}>
        <FromEm
          value={form.accent_color}
          em={data.em?.color}
          onReset={() => set('accent_color', data.em?.color ?? '#111827')}
        />
      </Box>

      {/* Banner: colour above, or a background image */}
      <ImageSlot
        title="Bandeau du billet"
        hint="Sans image, le bandeau prend la couleur ci-dessus. Image large (ex. 1800 × 360 px), recadrée automatiquement."
        url={s.banner_url}
        wide
        onUpload={(f) =>
          run(
            ticketingRepository.uploadImage(eventId, 'banner', f),
            'Image du bandeau enregistrée.'
          )
        }
        onRemove={() =>
          run(
            ticketingRepository.deleteImage(eventId, 'banner'),
            'Image du bandeau retirée.'
          )
        }
      />

      {/* Venue plan, on the right of the ticket */}
      <ImageSlot
        title="Plan du salon (à droite du billet)"
        hint="Exportez le plan en PNG depuis Floor Map (Exporter → PNG), puis ajoutez-le ici."
        url={s.plan_url}
        onUpload={(f) =>
          run(
            ticketingRepository.uploadImage(eventId, 'plan', f),
            'Plan enregistré.'
          )
        }
        onRemove={() =>
          run(ticketingRepository.deleteImage(eventId, 'plan'), 'Plan retiré.')
        }
      />

      {/* Language toggle */}
      <ToggleButtonGroup
        size="small"
        exclusive
        value={lang}
        onChange={(_, v) => v && setLang(v)}
      >
        <ToggleButton value="fr">Français</ToggleButton>
        <ToggleButton value="en">English</ToggleButton>
      </ToggleButtonGroup>

      {/* Practical info */}
      <TextField
        multiline
        minRows={5}
        size="small"
        label="Informations pratiques imprimées sur le billet"
        helperText="Horaires, adresse, accès, parking… En anglais, vide = le français."
        value={form[`info_${lang}`]}
        onChange={(e) => set(`info_${lang}`, e.target.value)}
      />

      {/* Intro text */}
      <TextField
        multiline
        minRows={2}
        size="small"
        label="Texte au-dessus du formulaire d'inscription"
        value={form[`intro_${lang}`]}
        onChange={(e) => set(`intro_${lang}`, e.target.value)}
      />

      {/* Contact and terms */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <Box>
          <TextField
            size="small"
            fullWidth
            label="E-mail de contact"
            value={form.contact_email}
            onChange={(e) => set('contact_email', e.target.value)}
          />
          <FromEm
            value={form.contact_email}
            em={data.em?.contact_email}
            onReset={() => set('contact_email', data.em?.contact_email ?? '')}
          />
        </Box>
        <TextField
          size="small"
          label="Lien des CGV"
          placeholder="https://"
          value={form.terms_url}
          onChange={(e) => set('terms_url', e.target.value)}
        />
        <Select
          size="small"
          value={form.currency}
          onChange={(e) => set('currency', e.target.value)}
          label="Devise"
        >
          {['EUR', 'CHF', 'GBP', 'USD', 'JPY'].map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          type="number"
          label="TVA incluse (%)"
          value={form.vat_rate}
          onChange={(e) => set('vat_rate', Number(e.target.value))}
        />
      </Box>

      {/* Preview button */}
      <Button
        size="small"
        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        onClick={() => preview()}
      >
        Voir un billet exemple (PDF)
      </Button>
    </Box>
  );
}

export default TicketDesignSection;
